import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, Lock, Mail, Mailbox, MapPin, Phone, Stamp, X } from 'lucide-react';
import { useDialogTrap } from '../letters/useDialogTrap';
import { toiypAddress } from './share';
import { ALL_HOW_CARDS, type HowCard } from './steps';

/**
 * "How toiyp works" — three swipeable cards (onboarding plan, slice 1). Used
 * twice: as the first step of the first-run tour, and behind "How does it
 * work?" on the sign-in screen. The visuals are static stand-ins for the real
 * letter UI, so they render signed-out too.
 */

const COPY: Record<HowCard, { title: string; body: string }> = {
  address: {
    title: 'Give your toiyp, not your number',
    body: "Bumped into an old friend? Met someone nice? Hand them your toiyp. They write to you here, and you decide when to share more.",
  },
  post: {
    title: 'Letters take their time',
    body: 'Every letter is collected and delivered like real post, between 8am and 5pm, Monday to Saturday. No read ticks, no typing dots. Just write properly.',
  },
  trust: {
    title: 'Trust grows as you write',
    body: "The more you two write, the closer you get: from New to Hot to In trust. Share your phone number or address only when you're ready.",
  },
};

function AddressVisual({ handle }: { handle?: string }) {
  return (
    <div className="how-visual how-visual--address" aria-hidden="true">
      <span className="how-visual__number">
        <Phone size={13} /> +44 7700 900…
      </span>
      <span className="how-visual__address">
        <Mail size={15} />
        <span className="toiyp-address">{toiypAddress(handle || 'you')}</span>
      </span>
    </div>
  );
}

function PostVisual() {
  return (
    <div className="how-visual how-visual--post" aria-hidden="true">
      <div className="sealed-envelope how-visual__envelope">
        <div className="sealed-envelope__top">
          <span className="sealed-envelope__label">
            <Lock size={10} /> your letter
          </span>
          <span className="pill pill--transit">in transit</span>
        </div>
        <span className="in-the-post">
          <span className="in-the-post__route">
            <span className="in-the-post__end">
              <Stamp size={12} />
            </span>
            <span className="in-the-post__track">
              <span className="in-the-post__fill" style={{ width: '58%' }} />
              <span className="in-the-post__envelope" style={{ left: '58%' }}>
                <Mail size={13} />
              </span>
            </span>
            <span className="in-the-post__end in-the-post__end--box">
              <Mailbox size={12} />
            </span>
          </span>
          <span className="in-the-post__labels">
            <span className="in-the-post__stage">on its way</span>
            <span className="in-the-post__eta">arrives tomorrow</span>
          </span>
        </span>
        <div className="sealed-envelope__bottom">
          <span className="bubble__time">sealed 4:12pm</span>
          <span className="pill pill--eta">arrives 10:40am</span>
        </div>
      </div>
    </div>
  );
}

function TrustVisual() {
  return (
    <div className="how-visual how-visual--trust" aria-hidden="true">
      <div className="how-visual__tiers">
        <span className="pill pill--tier-new">New</span>
        <ArrowRight size={14} className="how-visual__arrow" />
        <span className="pill pill--tier-hot">Hot</span>
        <ArrowRight size={14} className="how-visual__arrow" />
        <span className="pill pill--tier-trusted">In trust</span>
      </div>
      <div className="how-visual__unlocks">
        <span>
          <Phone size={12} /> phone number
        </span>
        <span>
          <MapPin size={12} /> address
        </span>
        <span className="how-visual__unlocks-note">when you say so</span>
      </div>
    </div>
  );
}

export function HowCardVisual({ card, handle }: { card: HowCard; handle?: string }) {
  if (card === 'address') return <AddressVisual handle={handle} />;
  if (card === 'post') return <PostVisual />;
  return <TrustVisual />;
}

/** Position dots: where you are in a short run of steps or cards. */
export function Dots({ count, at, label }: { count: number; at: number; label: string }) {
  return (
    <span className="how-dots" role="img" aria-label={`${label} ${at + 1} of ${count}`}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className={`how-dots__dot${i === at ? ' how-dots__dot--on' : ''}`} />
      ))}
    </span>
  );
}

/**
 * The swipeable track. Controlled: the parent owns which card is showing, so
 * a "Next" button outside the track and a swipe inside it agree.
 */
export function HowItWorksCards({
  cards = ALL_HOW_CARDS,
  index,
  onIndexChange,
  handle,
}: {
  cards?: readonly HowCard[];
  index: number;
  onIndexChange: (i: number) => void;
  handle?: string;
}) {
  const track = useRef<HTMLDivElement>(null);

  // A Next tap (or a dot) moves the track; a swipe reports back below.
  useEffect(() => {
    const el = track.current;
    if (!el) return;
    const target = index * el.clientWidth;
    if (Math.abs(el.scrollLeft - target) > 4) el.scrollTo({ left: target, behavior: 'smooth' });
  }, [index]);

  const onScroll = useCallback(() => {
    const el = track.current;
    if (!el || el.clientWidth === 0) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== index && Math.abs(el.scrollLeft - i * el.clientWidth) < 4) onIndexChange(i);
  }, [index, onIndexChange]);

  return (
    <div className="how">
      <div className="how__track" ref={track} onScroll={onScroll}>
        {cards.map((card, i) => (
          <section
            key={card}
            className="how__card"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${cards.length}`}
            aria-hidden={i !== index}
          >
            <HowCardVisual card={card} handle={handle} />
            <h2 className="how__title">{COPY[card].title}</h2>
            <p className="how__body">{COPY[card].body}</p>
          </section>
        ))}
      </div>
      <Dots count={cards.length} at={index} label="Card" />
    </div>
  );
}

/** The sign-in screen's "How does it work?" — the same cards, in a sheet. */
export function HowItWorksDialog({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  useDialogTrap(ref, onClose);
  const last = index === ALL_HOW_CARDS.length - 1;

  return (
    <div className="how-dialog__backdrop" onClick={onClose}>
      <div
        className="how-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="How toiyp works"
        tabIndex={-1}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="how-dialog__close icon-btn" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <HowItWorksCards index={index} onIndexChange={setIndex} />
        <button
          type="button"
          className="btn btn--primary btn--block"
          onClick={() => (last ? onClose() : setIndex(index + 1))}
        >
          {last ? 'Got it' : 'Next'}
        </button>
      </div>
    </div>
  );
}
